import React, { useEffect, useRef, useState } from 'react';
import { Option, Quiz, QuizStatus } from './quiztypes';
import BizQuestion from './question';
import Highscore from './highscore';
import { useCountUp } from 'react-countup';
import { EngageObjectType, EngageObjectUserStatus } from '../../types';
import { QuizConfig } from './config';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import BizPhrase from '../../Components/Phrase';
import { getPathWSiteRoot } from '../../../Js/React/Services/common';
import { getAngularJsService, goto } from '../../../Js/React/Services/legacy';
import { logEngageUserStatus } from '../../Common/Services/engage';

interface SpeedquizProps {
	quizId: number;
	fromOneQuiz: boolean;
}

interface QuizResponse {
	data: Quiz;
}

const BizSpeedquiz = ({ quizId, fromOneQuiz }: SpeedquizProps) => {
	const quizService = getAngularJsService('modules.speedquiz.service');

	const [quiz, setQuiz] = useState<Quiz>();
	const [quizStatus, setQuizStatus] = useState<QuizStatus>({
		IsInitialized: false,
		Points: -1,
		LivesLeft: -1,
		QuestionIdsLeft: [],
		IsGameOver: false,
		IsResultSubmitted: false,
	});
	const [playInitCounter, setPlayInitCounter] = useState(false);

	const countUpRef = useRef(null);
	const { start: startCounter, update: updateCounter } = useCountUp({
		ref: countUpRef,
		start: 0,
		end: 0,
		delay: QuizConfig.PointsCounterDelayInSec,
		duration: QuizConfig.PointsCounterDurationInSec,
		suffix: 'p',
	});

	const statusRef = useRef(quizStatus);
	statusRef.current = quizStatus;

	const quizCleanup = () => {
		console.log('quiz cleanup');
	};

	const restartQuizHandler = () => {
		//reset
		setQuizStatus({
			IsInitialized: false,
			Points: -1,
			LivesLeft: -1,
			QuestionIdsLeft: [],
			IsGameOver: false,
			IsResultSubmitted: false,
		});

		//init
		initQuiz();
	};

	const initQuiz = () => {
		setPlayInitCounter(true);
	};

	const startQuiz = () => {
		if (quiz && quiz.Questions && quiz.Questions.length > 0) {
			logEngageUserStatus(quiz.PluginInstanceId, EngageObjectType.Speedquiz, EngageObjectUserStatus.Started, false);
			setQuizStatus({
				Points: 0,
				LivesLeft: quiz.MaxLives,
				QuestionIdsLeft: quiz.Questions.map((q) => q.Id),
				IsInitialized: true,
				IsGameOver: false,
				IsResultSubmitted: false,
			});
			startCounter();
		}
	};

	useEffect(() => {
		quizCleanup(); //just in case

		return () => {
			//never run, for some reason :(
			console.log('unmount quiz');
			quizCleanup();
		};
	}, []);

	useEffect(() => {
		if (quizId > 0) {
			console.log('Loading speedquiz', quizId);
			quizService.GetSpeedquizInstance(quizId).then(({ data }: QuizResponse) => {
				console.log('Retrieved quiz', data);
				setQuiz(data);
			});
		}
	}, [quizId]);

	useEffect(() => {
		initQuiz();
	}, [quiz?.Id]);

	useEffect(() => {
		if (quizStatus.IsGameOver && quiz) {
			console.log('game over');

			logEngageUserStatus(
				quiz.PluginInstanceId,
				EngageObjectType.Speedquiz,
				EngageObjectUserStatus.Finished,
				quizStatus.Points > 0
			);
			quizService
				.Submit({
					PluginInstanceId: quiz.PluginInstanceId,
					Score: quizStatus.Points,
					QuestionsPresented: 0, //todo, but wasnt working in old version either
				})
				.then(() => {
					setQuizStatus({
						...quizStatus,
						IsResultSubmitted: true,
					});
				});
		}
	}, [quizStatus.IsGameOver]);

	useEffect(() => {
		console.log('Question ids left changed, setting new current question');
		if (quiz && quizStatus.IsInitialized && quizStatus.QuestionIdsLeft.length > 0) {
			const nextQuestionId = quizStatus.QuestionIdsLeft[Math.floor(Math.random() * quizStatus.QuestionIdsLeft.length)];
			const question = quiz.Questions.find((q) => q.Id === nextQuestionId);
			if (question) {
				setQuizStatus({
					...quizStatus,
					CurrentQuestion: question,
				});
			}
		} else if (quiz && quizStatus.IsInitialized && quizStatus.QuestionIdsLeft.length === 0) {
			setQuizStatus({
				...quizStatus,
				IsGameOver: true,
			});
		}
	}, [quizStatus.QuestionIdsLeft.length]);

	const questionAnsweredHandler = (option: Option, elapsedTimeMS: number) => {
		if (!quizStatus.IsInitialized) return;
		if (!quizStatus.CurrentQuestion) return;
		if (quizStatus.CurrentQuestion.Id !== option.SpeedquizQuestionId) return;

		let newPoints = quizStatus.Points;
		let questionPoints = 0;
		let newLives = quizStatus.LivesLeft;

		if (option.IsCorrectAnswer) {
			console.log(`Question answered correctly in ${elapsedTimeMS}ms`);

			const allowedMS = (quiz?.DefaultTime ?? 0) * 1000;
			const timeLeftMS = allowedMS - elapsedTimeMS;
			console.log(`Question answered with ${timeLeftMS}ms left`);
			if (timeLeftMS > 0) {
				const timeLeftPercent = timeLeftMS / allowedMS;
				console.log(`Question answered with ${timeLeftPercent}% left`);

				questionPoints = Math.floor(timeLeftPercent * (quiz?.DefaultScore ?? 0) * 100);
				console.log(`Question gives ${questionPoints} points`);

				newPoints += questionPoints;
			} else {
				console.log('Answered too late, no points');
			}
		} else {
			console.log('Question answered wrong, no points');
			newLives--;
		}

		quizService.Answer({
			SpeedquizQuestionId: option.SpeedquizQuestionId,
			SpeedquizOptionId: option.Id,
			Score: questionPoints,
		});

		setQuizStatus({
			...quizStatus,
			Points: newPoints,
			LivesLeft: newLives,
		});

		updateCounter(newPoints);

		if (newLives > 0) {
			const timeout = setTimeout(() => {
				const qIdsLeft = quizStatus.QuestionIdsLeft.filter((q) => q !== option.SpeedquizQuestionId);
				if (!statusRef.current) return;

				const newStatus = {
					...statusRef.current,
					QuestionIdsLeft: qIdsLeft,
				};

				setQuizStatus(newStatus);
				clearTimeout(timeout);
			}, QuizConfig.DelayAfterAnswerInMS);
		} else {
			const timeout = setTimeout(() => {
				if (!statusRef.current) return;

				const newStatus = {
					...statusRef.current,
					IsGameOver: true,
					QuestionIdsLeft: [],
				};

				setQuizStatus(newStatus);
				clearTimeout(timeout);
			}, QuizConfig.DelayAfterAnswerInMS);
		}
	};

	const onQuestionTimesUpHandler = () => {
		setQuizStatus({
			...quizStatus,
			IsGameOver: true,
			QuestionIdsLeft: [],
		});
	};

	const goBackHandler = () => {
		if (fromOneQuiz && quiz) {
			goto('base.modules.speedquizone', { urlRewrite: quiz.UrlRewrite, pluginInstanceId: quiz.PluginInstanceId });
		} else {
			goto('base.modules.speedquiz');
		}
	};

	return (
		<div id='speedquiz-play' className='speedquiz-container'>
			<div className='quiz-header'>
				<div className={quizStatus.IsInitialized && !quizStatus.IsGameOver ? 'quiz-points show' : 'quiz-points'}>
					<h4 ref={countUpRef}></h4>
				</div>
				<div className='quiz-logo'>
					<img src={getPathWSiteRoot('Images/Speedquiz.png')} />
				</div>
				<div className={quizStatus.IsInitialized && !quizStatus.IsGameOver ? 'quiz-lives show' : 'quiz-lives'}>
					{quiz &&
						quiz.MaxLives > 0 &&
						[...Array(quiz.MaxLives)].map((e, index) => (
							<img
								src={getPathWSiteRoot('Images/heart.png')}
								className={index + 1 > quizStatus.LivesLeft ? 'life gone' : 'life'}
								key={index}
							/>
						))}
				</div>
			</div>
			{!quizStatus.IsInitialized && (
				<div className='quiz-init-counter'>
					<CountdownCircleTimer
						isPlaying={playInitCounter}
						duration={QuizConfig.InitCountdownConfig.Seconds}
						colors={QuizConfig.InitCountdownConfig.Colors}
						colorsTime={QuizConfig.InitCountdownConfig.ColorTime}
						onComplete={startQuiz}
					>
						{({ remainingTime }) => <span className='quiz-init-counter-time'>{remainingTime}</span>}
					</CountdownCircleTimer>
				</div>
			)}
			{quizStatus.CurrentQuestion && !quizStatus.IsGameOver && (
				<BizQuestion
					question={quizStatus.CurrentQuestion}
					maxTimeInSec={quiz?.DefaultTime ?? 0}
					onQuestionAnswered={questionAnsweredHandler}
					onTimesUp={onQuestionTimesUpHandler}
				></BizQuestion>
			)}
			{quiz && quizStatus.IsInitialized && quizStatus.IsGameOver && quizStatus.IsResultSubmitted && (
				<div className='quiz-gameover'>
					<div className='quiz-result'>
						<span>
							<BizPhrase
								phraseKey='SPEEDQUIZ.RESULT.YOUR_POINTS'
								placeholderValues={[{ Key: '##POINTS##', Value: quizStatus.Points.toString() }]}
							></BizPhrase>
						</span>
					</div>
					<div className='quiz-name'>
						<span>{quiz?.Name}</span>
					</div>
					<Highscore pluginInstanceId={quiz?.PluginInstanceId}></Highscore>
					<div className='quiz-back'>
						<span className='biz-btn biz-primary' onClick={restartQuizHandler}>
							<BizPhrase phraseKey='SPEEDQUIZ.BTN_PLAY_AGAIN'></BizPhrase>
						</span>
						<span className='biz-btn biz-secondary' onClick={goBackHandler}>
							<BizPhrase phraseKey='SPEEDQUIZ.BTN_BACK'></BizPhrase>
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default BizSpeedquiz;
